import { useEffect, useState } from "react";
import {
  Trash2,
  RefreshCw,
  Server,
  Plus,
  Boxes,
  Image as ImageIcon,
  Database,
  Network as NetworkIcon,
  Layers,
  DownloadCloud,
} from "lucide-react";
import { DockerHostWizard } from "@/components/docker/DockerHostWizard";
import { LiveContainersPanel } from "@/components/docker/LiveContainersPanel";
import { ImagesPanel } from "@/components/docker/ImagesPanel";
import { VolumesPanel } from "@/components/docker/VolumesPanel";
import { NetworksPanel } from "@/components/docker/NetworksPanel";
import { SystemDfBar } from "@/components/docker/SystemDfBar";
import {
  useDockerHosts,
  useDockerBilling,
  useDeleteDockerHost,
  useRedeployAgent,
  useStacks,
  useDeployStack,
  useStackAction,
  useDeleteStack,
} from "@/hooks/useDocker";
import type { StackAction } from "@/types/docker";

type Tab = "containers" | "images" | "volumes" | "networks" | "stacks";

const TABS: { key: Tab; label: string; icon: typeof Boxes }[] = [
  { key: "containers", label: "Conteneurs", icon: Boxes },
  { key: "images", label: "Images", icon: ImageIcon },
  { key: "volumes", label: "Volumes", icon: Database },
  { key: "networks", label: "Networks", icon: NetworkIcon },
  { key: "stacks", label: "Stacks", icon: Layers },
];

function statusColor(status: string): string {
  if (status === "running" || status === "up" || status === "ready")
    return "bg-green-900/40 text-green-300";
  if (status === "error" || status === "agent_error")
    return "bg-red-900/40 text-red-300";
  return "bg-gray-800 text-gray-400";
}

export default function Containers() {
  const { data: hosts, isLoading, refetch } = useDockerHosts();
  const [hostId, setHostId] = useState<number | undefined>();
  const [tab, setTab] = useState<Tab>("containers");
  const [showWizard, setShowWizard] = useState(false);

  useEffect(() => {
    if (hostId === undefined && hosts && hosts.length > 0) setHostId(hosts[0].id);
  }, [hosts, hostId]);

  const hostDelete = useDeleteDockerHost();
  const redeployAgent = useRedeployAgent();
  const { data: billing } = useDockerBilling();
  const ready = hosts?.find((h) => h.id === hostId)?.status === "ready";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Docker</h1>
          <p className="mt-1 text-sm text-gray-400">
            Explorateur des docker-hosts — conteneurs, images, volumes, réseaux
          </p>
          {billing && (
            <p className="mt-1 text-sm text-green-300">
              Coût conteneurs : {billing.total_monthly.toFixed(2)} €/mois
              <span className="text-gray-500">
                {" "}
                ({billing.containers.length} conteneur
                {billing.containers.length > 1 ? "s" : ""})
              </span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowWizard(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-500"
          >
            <Plus className="h-4 w-4" />
            Nouveau docker-host
          </button>
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-700 px-4 py-2 text-sm font-medium text-gray-300 transition-colors hover:bg-gray-800"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {isLoading && <p className="text-gray-400">Chargement…</p>}

      {hosts && hosts.length === 0 && (
        <p className="text-gray-400">
          Aucun docker-host. Provisionnez-en un.
        </p>
      )}

      {/* host selector */}
      {hosts && hosts.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {hosts.map((h) => (
            <div
              key={h.id}
              onClick={() => setHostId(h.id)}
              className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                h.id === hostId
                  ? "border-blue-500 bg-blue-900/30 text-blue-200"
                  : "border-gray-700 text-gray-300 hover:bg-gray-800"
              }`}
            >
              <Server className="h-4 w-4" />
              {h.name}
              {h.service_tier === "C" && (
                <span
                  title="Tier C — paire actif/actif cross-site"
                  className="rounded bg-purple-900/40 px-2 py-0.5 text-xs text-purple-200"
                >
                  HA
                </span>
              )}
              <span
                className={`rounded px-2 py-0.5 text-xs ${statusColor(h.status)}`}
              >
                {h.status}
              </span>
              <button
                title="Redéployer l'agent (pousse la dernière version sur l'hôte)"
                onClick={(e) => {
                  e.stopPropagation();
                  redeployAgent.mutate(h.id);
                }}
                className="ml-1 rounded p-0.5 text-gray-500 hover:bg-blue-900/40 hover:text-blue-300"
              >
                <DownloadCloud className="h-3.5 w-3.5" />
              </button>
              <button
                title="Supprimer l'hôte (détruit la VM)"
                onClick={(e) => {
                  e.stopPropagation();
                  if (
                    window.confirm(
                      `Supprimer le docker-host « ${h.name} » ? La VM, ses conteneurs et stacks seront détruits.`,
                    )
                  ) {
                    hostDelete.mutate(h.id, {
                      onSuccess: () => {
                        if (hostId === h.id) setHostId(undefined);
                      },
                    });
                  }
                }}
                className="ml-1 rounded p-0.5 text-gray-500 hover:bg-red-900/40 hover:text-red-300"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {hostId && ready && <SystemDfBar hostId={hostId} />}

      {hostId && !ready && (
        <p className="rounded-lg border border-yellow-900/50 bg-yellow-900/10 px-4 py-2 text-sm text-yellow-300">
          L'hôte n'est pas encore prêt (agent indisponible) — l'exploration live
          sera active une fois le statut « ready ».
        </p>
      )}

      {/* tabs */}
      {hostId && ready && (
        <>
          <div className="flex gap-1 border-b border-gray-800">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm ${
                  tab === t.key
                    ? "border-blue-500 text-blue-300"
                    : "border-transparent text-gray-400 hover:text-gray-200"
                }`}
              >
                <t.icon className="h-4 w-4" />
                {t.label}
              </button>
            ))}
          </div>

          {tab === "containers" && <LiveContainersPanel hostId={hostId} />}
          {tab === "images" && <ImagesPanel hostId={hostId} />}
          {tab === "volumes" && <VolumesPanel hostId={hostId} />}
          {tab === "networks" && <NetworksPanel hostId={hostId} />}
          {tab === "stacks" && <StacksPanel hostId={hostId} />}
        </>
      )}

      {showWizard && <DockerHostWizard onClose={() => setShowWizard(false)} />}
    </div>
  );
}

// --- Stacks (AlphaProx-managed compose stacks) -----------------------------

function StacksPanel({ hostId }: { hostId: number }) {
  const { data: stacks } = useStacks(hostId);
  const deployStack = useDeployStack();
  const sAction = useStackAction();
  const sDelete = useDeleteStack();
  const [newStack, setNewStack] = useState({ name: "", compose_yaml: "" });

  function submitStack() {
    if (!newStack.name || !newStack.compose_yaml) return;
    deployStack.mutate(
      { hostId, payload: newStack },
      { onSuccess: () => setNewStack({ name: "", compose_yaml: "" }) },
    );
  }

  function statusColor(status: string): string {
    if (status === "up") return "bg-green-900/40 text-green-300";
    if (status === "error") return "bg-red-900/40 text-red-300";
    return "bg-gray-800 text-gray-400";
  }

  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-gray-200">Stacks Compose</h2>
      <div className="space-y-2 rounded-lg border border-gray-800 p-3">
        <input
          value={newStack.name}
          onChange={(e) =>
            setNewStack((s) => ({ ...s, name: e.target.value }))
          }
          placeholder="nom du stack"
          className="w-48 rounded border border-gray-700 bg-gray-900 px-2 py-1 text-sm text-gray-100 placeholder-gray-500"
        />
        <textarea
          value={newStack.compose_yaml}
          onChange={(e) =>
            setNewStack((s) => ({ ...s, compose_yaml: e.target.value }))
          }
          placeholder={"services:\n  web:\n    image: nginx:alpine"}
          rows={5}
          className="w-full rounded border border-gray-700 bg-gray-900 px-2 py-1 font-mono text-xs text-gray-100 placeholder-gray-500"
        />
        <button
          onClick={submitStack}
          disabled={
            !newStack.name || !newStack.compose_yaml || deployStack.isPending
          }
          className="inline-flex items-center gap-1 rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-500 disabled:opacity-50"
        >
          <Plus className="h-3.5 w-3.5" />
          Déployer
        </button>
      </div>
      <div className="overflow-hidden rounded-lg border border-gray-800">
        <table className="min-w-full divide-y divide-gray-800 text-sm">
          <thead className="bg-gray-900/60 text-left text-gray-400">
            <tr>
              <th className="px-4 py-2">Nom</th>
              <th className="px-4 py-2">Statut</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {(stacks ?? []).map((s) => (
              <tr key={s.id}>
                <td className="px-4 py-2 text-gray-100">{s.name}</td>
                <td className="px-4 py-2">
                  <span
                    className={`rounded px-2 py-0.5 text-xs ${statusColor(s.status)}`}
                  >
                    {s.status}
                  </span>
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center justify-end gap-2 text-gray-400">
                    {(["up", "restart", "down"] as StackAction[]).map((a) => (
                      <button
                        key={a}
                        onClick={() => sAction.mutate({ id: s.id, action: a })}
                        className="rounded px-2 py-1 text-xs hover:bg-gray-800 hover:text-gray-100"
                      >
                        {a}
                      </button>
                    ))}
                    <button
                      title="delete"
                      onClick={() => sDelete.mutate({ id: s.id })}
                      className="rounded p-1 hover:bg-red-900/40 hover:text-red-300"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {stacks && stacks.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-3 text-gray-500">
                  Aucun stack.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
